import { useEffect, useState, useContext, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import DOMPurify from 'dompurify';
import Layout from '../components/Layout';
import ParallaxBanner from '../components/ParallaxBanner';
import GalleryCarousel from '../components/GalleryCarousel';
import ProductVideo from '../components/ProductVideo';
import WishlistButton from '../components/WishlistButton';
import ReviewSummary from '../components/reviews/ReviewSummary';
import ReviewList from '../components/reviews/ReviewList';
import ReviewForm from '../components/reviews/ReviewForm';
import { CartContext } from '../contexts/CartContext';
import productService from '../services/productService';
import reviewService from '../services/reviewService';
import contentService from '../services/contentService';
import featureFlags from '../utils/featureFlags';
import { formatPrice, isInStock, getStockDisplay, getProductImages, handleImageError, addCacheBuster } from '../utils/apiHelpers';
import { trackViewItem, trackAddToCart } from '../utils/analytics';
import useContentUpdates from '../hooks/useContentUpdates';

// Hardcoded content - preserved for fallback if API doesn't return these fields
const HARDCODED_PRODUCT_DETAILS = {
  features: [
    '100% Polyester material Canvas',
    'Brail Markings for the visually impaired',
    '14 days return policy'
  ],
  aboutTheArt: null, // Will show default text if null
  additionalInformation: null, // Will show default text if null
  reviewCount: 0
};

function ProductDetail() {
  const { id } = useParams();
  const { addToCart, loading: cartLoading } = useContext(CartContext);
  const [quantity, setQuantity] = useState(1);
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reviewCount, setReviewCount] = useState(0);
  const [imageRefreshKey, setImageRefreshKey] = useState(Date.now());
  const [backgroundUrl, setBackgroundUrl] = useState(null);
  const reviewListRef = useRef(null);

  // Fetch product data from backend - extracted to reusable function for WebSocket updates
  const fetchProduct = useCallback(async (showLoading = true) => {
    if (!id) return;

    try {
      if (showLoading) {
        setLoading(true);
        setError(null);
      }
      const response = await productService.getProduct(id);
      if (response.success && response.product) {
        setProduct(response.product);
        // Track product view only on initial load
        if (showLoading) {
          trackViewItem(response.product);
        }
      } else {
        setError(response.error || 'Product not found');
      }
    } catch (err) {
      console.error('[ProductDetail] Error fetching product:', err);
      setError('Failed to load product');
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  }, [id]);

  // Initial fetch on mount
  useEffect(() => {
    fetchProduct();
  }, [fetchProduct]);

  // Fetch review count when product loads
  useEffect(() => {
    if (product?.id) {
      reviewService.getReviewSummary(product.id)
        .then(response => {
          if (response.data) {
            setReviewCount(response.data.totalReviews || 0);
          }
        })
        .catch(error => {
          console.error('[ProductDetail] Error fetching review count:', error);
          setReviewCount(0);
        });
    }
  }, [product?.id]);

  // Fetch page background from CMS
  const fetchBackground = useCallback(async () => {
    if (!featureFlags.useDynamicBackgrounds()) {
      return;
    }

    try {
      const result = await contentService.getPageBackground('product-listing');
      if (result.success && result.imageUrl) {
        setBackgroundUrl(result.imageUrl);
      }
    } catch (err) {
      console.error('[ProductDetail] Error fetching background:', err);
    }
  }, []);

  useEffect(() => {
    fetchBackground();
  }, [fetchBackground]);

  // Listen for real-time product updates via WebSocket
  useContentUpdates({
    onProductUpdated: (productId, _sku, name) => {
      // Refresh product if this is the one being viewed
      if (productId === parseInt(id)) {
        if (process.env.NODE_ENV === 'development') {
          console.log(`[ProductDetail] Real-time update received for product ${productId} - ${name}`);
        }
        fetchProduct(false); // Refetch without showing loading spinner
        setImageRefreshKey(Date.now()); // Force image cache refresh
        toast.info('Product information has been updated', { autoClose: 2000 });
      }
    },
    onProductDeleted: (productId) => {
      // Handle product deletion while viewing
      if (productId === parseInt(id)) {
        if (process.env.NODE_ENV === 'development') {
          console.log(`[ProductDetail] Product ${productId} was deleted`);
        }
        setError('This product is no longer available');
        toast.warning('This product has been removed', { autoClose: 3000 });
      }
    },
    onReviewApproved: (reviewId, productId) => {
      // Refresh review list if this product's review was approved
      if (productId === parseInt(id)) {
        if (process.env.NODE_ENV === 'development') {
          console.log(`[ProductDetail] Review ${reviewId} approved for product ${productId}`);
        }
        // Refresh review list
        if (reviewListRef.current?.refresh) {
          reviewListRef.current.refresh();
        }
        // Refresh review count
        reviewService.getReviewSummary(productId)
          .then(response => {
            if (response.data) {
              setReviewCount(response.data.totalReviews || 0);
            }
          })
          .catch(error => {
            console.error('[ProductDetail] Error refreshing review count:', error);
          });
        setImageRefreshKey(Date.now()); // Force review image cache refresh
        toast.success('A new review has been published!', { autoClose: 2000 });
      }
    },
    onReviewRejected: (reviewId, productId) => {
      // Handle review rejection (mostly for user notification)
      if (productId === parseInt(id)) {
        if (process.env.NODE_ENV === 'development') {
          console.log(`[ProductDetail] Review ${reviewId} rejected for product ${productId}`);
        }
        // Optionally show notification (mainly for debugging)
        if (process.env.NODE_ENV === 'development') {
          toast.info(`Review ${reviewId} was not approved`, { autoClose: 2000 });
        }
      }
    },
    onReviewUnpublished: (reviewId, productId) => {
      // Refresh review list if this product's review was unpublished
      if (productId === parseInt(id)) {
        if (process.env.NODE_ENV === 'development') {
          console.log(`[ProductDetail] Review ${reviewId} unpublished for product ${productId}`);
        }
        // Refresh review list
        if (reviewListRef.current?.refresh) {
          reviewListRef.current.refresh();
        }
        // Refresh review count
        reviewService.getReviewSummary(productId)
          .then(response => {
            if (response.data) {
              setReviewCount(response.data.totalReviews || 0);
            }
          })
          .catch(error => {
            console.error('[ProductDetail] Error refreshing review count:', error);
          });
        setImageRefreshKey(Date.now()); // Force review image cache refresh
        toast.info('A review has been unpublished', { autoClose: 2000 });
      }
    },

    onPageBackgroundUpdated: (page) => {
      if (page === 'product-detail') {
        fetchBackground();
      }
    }
  });

  // Handle quantity decrease
  const handleDecrease = () => {
    setQuantity(prev => Math.max(1, prev - 1));
  };

  // Handle quantity increase
  const handleIncrease = () => {
    setQuantity(prev => Math.min(10, prev + 1));
  };

  // Handle quantity input change
  const handleQuantityChange = (e) => {
    const value = parseInt(e.target.value) || 1;
    setQuantity(Math.max(1, Math.min(10, value)));
  };

  // Handle add to cart
  const handleAddToCart = async () => {
    if (!product) return;

    const result = await addToCart(product.id, quantity);

    if (result.success) {
      toast.success(`${product.name} added to cart!`);
      // Track add to cart event
      trackAddToCart(product, quantity);
      setQuantity(1); // Reset quantity after adding
    } else {
      toast.error(result.error || 'Failed to add to cart');
    }
  };
  useEffect(() => {
    // Handle window load event (fade out loader)
    const handleWindowLoad = () => {
      if (window.$ && window.$('.loader1').length > 0) {
        window.$(".loader1").fadeOut(800);
        window.$('.side-menu').removeClass('hidden');
      }
    };

    // If window is already loaded, trigger immediately
    if (document.readyState === 'complete') {
      handleWindowLoad();
    } else {
      window.addEventListener('load', handleWindowLoad);
    }

    // Initialize WOW animations
    if (window.WOW) {
      new window.WOW().init();
    }

    return () => {
      window.removeEventListener('load', handleWindowLoad);
    };
  }, []);

  // Initialize Swiper after product loads
  useEffect(() => {
    if (!loading && product) {
      const initSwiper = () => {
        if (window.Swiper && window.$) {
          if (window.$('#gallery').length) {
            const galleryTop = new window.Swiper('#gallery', {
              effect: 'fade',
              direction: 'vertical',
              spaceBetween: 10,
              slidesPerView: 1,
              slidesPerGroup: 1,
              loop: true,
              initialSlide: 0,
              centeredSlides: true,
              loopAdditionalSlides: 5,
              touchRatio: 0.2,
            });

            const galleryThumbs = new window.Swiper('#thumbs', {
              direction: 'vertical',
              spaceBetween: 10,
              slidesPerView: 3,
              slidesPerGroup: 1,
              loop: true,
              initialSlide: 0,
              centeredSlides: true,
              loopAdditionalSlides: 3,
              touchRatio: 0.2,
              slideToClickedSlide: true
            });

            // Sync the two sliders using controller
            galleryTop.controller.control = galleryThumbs;
            galleryThumbs.controller.control = galleryTop;
          }
        }
      };

      // Small delay to ensure DOM is updated
      setTimeout(initSwiper, 100);
    }
  }, [loading, product]);

  // Get product images using the apiHelpers function (maps CDN to local)
  const productImages = getProductImages(product);

  // Get category name from product
  const getCategoryName = () => {
    if (product?.category?.name) return product.category.name;
    return 'Products';
  };

  return (
    <Layout>
      {/* Parallax Banner */}
      {backgroundUrl && (
        <ParallaxBanner
          imageUrl={backgroundUrl}
          speed={0.6}
          offset={0}
          className="paralax-section-slide-data"
        />
      )}

      {/* START HEADING SECTION */}
      <div className="about_content">
        <div className="container">
          <div className="row">
            <div className="col-12">

              {/* Loading State */}
              {loading && (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-3 text-muted">Loading product...</p>
                </div>
              )}

              {/* Error State */}
              {error && !loading && (
                <div className="alert alert-warning text-center" role="alert">
                  {error}. <a href="/product-listing">Browse all products</a>
                </div>
              )}

              {/* Product Content */}
              {!loading && !error && product && (
              <div className="product-body">
                <nav aria-label="breadcrumb">
                  <ol className="breadcrumb text-center text-lg-left">
                    <li className="breadcrumb-item"><a href="/">Home</a></li>
                    <li className="breadcrumb-item"><a href="/product-listing">{getCategoryName()}</a></li>
                    <li className="breadcrumb-item active" aria-current="page">{product.name}</li>
                  </ol>
                </nav>
                <div className="pro-detail-sec row">
                  <div className="col-12">
                    <h4 className="pro-heading text-center text-lg-left">{product.name}</h4>
                    {product.shortDescription && (
                      <p className="pro-text text-center text-lg-left">{product.shortDescription}</p>
                    )}
                  </div>
                </div>
                <div className="row product-list product-detail">

                  <div className="col-12 col-lg-6 product-detail-slider">
                    <div className="wrapper">
                      <div className="Gallery swiper-container img-magnifier-container" id="gallery" style={{height: '360px'}}>
                        <div className="swiper-wrapper myimgs">
                          {productImages.map((imgUrl, index) => {
                            const cacheBustedUrl = addCacheBuster(imgUrl, imageRefreshKey);
                            return (
                              <div key={index} className="swiper-slide">
                                <a href={cacheBustedUrl} data-fancybox={index + 1} title="Zoom In">
                                  <img className="myimage" src={cacheBustedUrl} alt={`${product.name} - ${index + 1}`} onError={handleImageError} />
                                </a>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                      <div className="Thumbs swiper-container" id="thumbs" style={{height: '360px'}}>
                        <div className="swiper-wrapper">
                          {productImages.map((imgUrl, index) => {
                            const cacheBustedUrl = addCacheBuster(imgUrl, imageRefreshKey);
                            return (
                              <div key={index} className="swiper-slide">
                                <img src={cacheBustedUrl} alt={`${product.name} thumbnail - ${index + 1}`} onError={handleImageError} />
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>

                    {/* Product Video - Display if videoUrl exists */}
                    {product.videoUrl && (
                      <ProductVideo
                        videoUrl={product.videoUrl}
                        videoThumbnailUrl={product.videoThumbnailUrl}
                        productName={product.name}
                      />
                    )}
                  </div>

                  <div className="col-12 col-lg-6 text-center text-lg-left">
                    <div className="product-single-price">
                      <h4>
                        <span className="real-price">{formatPrice(product.price)}</span>
                        {product.compareAtPrice && (
                          <span><del>{formatPrice(product.compareAtPrice)}</del></span>
                        )}
                      </h4>
                      {product.description && (
                        <p className="pro-description">{product.description}</p>
                      )}
                      {/* Stock status */}
                      {(() => {
                        const stockInfo = getStockDisplay(product.stock, product.stockStatus);
                        return (
                          <p className={`mt-2 stock-status ${stockInfo.className}`}>
                            <strong>{stockInfo.text}</strong>
                          </p>
                        );
                      })()}

                      {/* Artist & Dimensions - Display if available */}
                      {(product.artist || product.dimensions) && (
                        <div className="product-meta mt-3">
                          {product.artist && (
                            <p className="mb-1"><strong>Artist:</strong> {product.artist}</p>
                          )}
                          {product.dimensions && (
                            <p className="mb-1"><strong>Dimensions:</strong> {product.dimensions}</p>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="product-checklist">
                      <ul>
                        {(product.features && product.features.length > 0
                          ? product.features
                          : HARDCODED_PRODUCT_DETAILS.features
                        ).map((feature, index) => (
                          <li key={index}>
                            <i className="fas fa-check"></i>
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="row product-quantity input_plus_mins no-gutters">

                      <div className="qty col-12 col-lg-3 d-lg-flex  align-items-lg-center d-inline-block">
                        <span
                          className="minus bg-dark"
                          onClick={handleDecrease}
                          style={{ cursor: 'pointer' }}
                          role="button"
                          aria-label="Decrease quantity"
                        >
                          <i className="lni-minus"></i>
                        </span>
                        <input
                          type="number"
                          className="count"
                          name="qty"
                          value={quantity}
                          onChange={handleQuantityChange}
                          min="1"
                          max="10"
                        />
                        <span
                          className="plus bg-dark"
                          onClick={handleIncrease}
                          style={{ cursor: 'pointer' }}
                          role="button"
                          aria-label="Increase quantity"
                        >
                          <i className="lni-plus"></i>
                        </span>
                      </div>
                      <div className="col-12 col-lg-9">
                        <div className="d-flex gap-2 align-items-center">
                          <button
                            className="btn pink-gradient-btn-into-black flex-grow-1"
                            onClick={handleAddToCart}
                            disabled={cartLoading || !isInStock(product)}
                          >
                            {cartLoading ? 'ADDING...' : (isInStock(product) ? 'ADD TO CART' : 'OUT OF STOCK')}
                          </button>
                          <WishlistButton
                            productId={product?.id}
                            showText={false}
                            iconSize="fa-lg"
                            className="product-detail-wishlist-btn"
                          />
                        </div>
                      </div>
                    </div>


                    <div className="dropdown-divider"></div>

                    <div className="product-tags-list">
                      <nav aria-label="breadcrumb">
                        <ol className="breadcrumb">
                          <li className="breadcrumb-item"><p className="d-inline">SKU: <span>{product.sku || product.id}</span></p></li>
                          {product.tags && product.tags.length > 0 && (
                            <li className="breadcrumb-item">
                              <span className="d-inline">Tags: {product.tags.map((tag, index) => (
                                <span key={tag}>
                                  <a href={`/product-listing?tag=${tag}`}>{tag}</a>
                                  {index < product.tags.length - 1 && <span className="comma-separtor">, </span>}
                                </span>
                              ))}</span>
                            </li>
                          )}
                        </ol>
                      </nav>
                    </div>

                    <div className="share-product-details">
                      <ul className="share-product-icons">
                        <li><p>Share Link:</p></li>
                        <li><a href="/" onClick={(e) => e.preventDefault()} className="facebook-bg-hvr" aria-label="Share on Facebook"><i className="fab fa-facebook-f" aria-hidden="true"></i></a></li>
                        <li><a href="/" onClick={(e) => e.preventDefault()} className="twitter-bg-hvr" aria-label="Share on Twitter"><i className="fab fa-twitter" aria-hidden="true"></i></a> </li>
                        <li><a href="/" onClick={(e) => e.preventDefault()} className="linkedin-bg-hvr" aria-label="Share on LinkedIn"><i className="fab fa-linkedin-in" aria-hidden="true"></i></a></li>
                        <li><a href="/" onClick={(e) => e.preventDefault()} className="instagram-bg-hvr" aria-label="Share on Instagram"><i className="fab fa-instagram" aria-hidden="true"></i></a></li>
                      </ul>
                    </div>
                  </div>

                  <div className="col-12 mt-4 mb-4">
                    <div className="row no-gutters product-all-details">

                      <ul className="col-12 nav nav-tabs" id="myTab" role="tablist">
                        <li className="col-4 nav-item">
                          <a className="nav-link active" id="home-tab" data-toggle="tab" href="#home" role="tab" aria-controls="home" aria-selected="true">About the Art</a>
                        </li>
                        <li className="col-4 nav-item">
                          <a className="nav-link" id="profile-tab" data-toggle="tab" href="#profile" role="tab" aria-controls="profile" aria-selected="false">Additional Information</a>
                        </li>
                        <li className="col-4 nav-item">
                          <a className="nav-link" id="contact-tab" data-toggle="tab" href="#contact" role="tab" aria-controls="contact" aria-selected="false">Customer Reviews ({reviewCount})</a>
                        </li>
                      </ul>
                      <div className="col-12 tab-content" id="myTabContent">
                        <div className="tab-pane fade show active" id="home" role="tabpanel" aria-labelledby="home-tab">
                          {product.aboutTheArt ? (
                            <div
                              className="product-tab-description text-center text-lg-left"
                              dangerouslySetInnerHTML={{
                                __html: DOMPurify.sanitize(product.aboutTheArt)
                              }}
                            />
                          ) : (
                            <p className="product-tab-description text-center text-lg-left">
                              This artwork captures a memorable moment in sports history.
                              Each piece is crafted with attention to detail and quality materials.
                            </p>
                          )}
                        </div>
                        <div className="tab-pane fade" id="profile" role="tabpanel" aria-labelledby="profile-tab">
                          <div className="row">
                            <div className="col-12">
                              {product.additionalInformation ? (
                                <div
                                  className="product-tab-description text-center text-lg-left"
                                  dangerouslySetInnerHTML={{
                                    __html: DOMPurify.sanitize(product.additionalInformation)
                                  }}
                                />
                              ) : (
                                <p className="product-tab-description text-center text-lg-left">
                                  No additional information available for this product.
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="tab-pane fade reviews" id="contact" role="tabpanel" aria-labelledby="contact-tab">

                          {/* Review Summary */}
                          <ReviewSummary productId={product.id} />

                          {/* Review List */}
                          <div className="mt-4">
                            <ReviewList
                              productId={product.id}
                              ref={reviewListRef}
                              imageRefreshKey={imageRefreshKey}
                            />
                          </div>

                          {/* Review Form */}
                          <div className="mt-5">
                            <ReviewForm
                              productId={product.id}
                              isLoggedIn={!!localStorage.getItem('authToken')}
                              onSuccess={() => {
                                // Refresh review list and count
                                if (reviewListRef.current?.refresh) {
                                  reviewListRef.current.refresh();
                                }
                                // Refetch review count
                                reviewService.getReviewSummary(product.id)
                                  .then(response => {
                                    if (response.data) {
                                      setReviewCount(response.data.totalReviews || 0);
                                    }
                                  })
                                  .catch(error => {
                                    console.error('[ProductDetail] Error refreshing review count:', error);
                                  });
                              }}
                            />
                          </div>

                        </div>
                      </div>

                    </div>
                  </div>

                </div>


              </div>
              )}

            </div>
          </div>
        </div>
        {/* START LATEST ARRIVALS - Using reusable component */}
        <GalleryCarousel />
        {/* END LATEST ARRIVALS */}
      </div>
      {/* END HEADING SECTION */}
    </Layout>
  );
}

export default ProductDetail;
